import type { State } from '../../State';

/** Configured item IDs for each promo group. */
export type PromoGroupItemsMap = {
  dailyFeatures: 'gifts' | 'friendInvite';
  recurringFeatures:
    | 'magicLamps'
    | 'streakQuest'
    | 'inviteChallenge'
    | 'jankenEventPromo'
    | 'gacha'
    | 'treasureExpress'
    | 'raceIntro'
    | 'championsLeague'
    | 'frenzy';
  ftueFeatures:
    | 'buddies'
    | 'dreamDrift'
    | 'superLollipop'
    | 'homeShortcut'
    | 'cards'
    | 'janken'
    | 'treasureExpress';
};

/** Identifier of a configured promo group. */
export type PromoGroupID = keyof PromoGroupItemsMap;

/** Identifier of an item configured in a promo group. */
export type PromoGroupItemID<GroupID extends PromoGroupID = PromoGroupID> =
  PromoGroupItemsMap[GroupID];

/** Group and item identifiers passed through promo group actions. */
export type PromoGroupItem = {
  groupId: PromoGroupID;
  itemId: PromoGroupItemID;
};

/** Launch slot allocation for one promo group. */
export type PromoGroupSlotConfig =
  | {
      /** Maximum slots this group may consume from the shared launch budget. */
      maxSlots: number;

      /** Shared-slot groups cannot also receive independent slots. */
      extraSlots?: never;
    }
  | {
      /** Independent slots available after the shared launch budget. */
      extraSlots: number;

      /** Extra-slot groups do not consume the shared launch budget. */
      maxSlots?: never;
    };

/** Configuration shared by all items in one promo group. */
export type PromoGroupConfig<GroupID extends PromoGroupID = PromoGroupID> =
  PromoGroupSlotConfig & {
    /** Default per-item cooldown in milliseconds. */
    cooldown: number;

    /** Ordering strategy applied after readiness is evaluated. */
    priorityMode?: 'configured' | 'neverSeenFirst';

    /** Whether undated unseen items should be randomly ordered. */
    randomizeUndatedTies?: boolean;

    /** Items in descending priority order. */
    items: {
      /** Unique identifier for this item within the group. */
      id: PromoGroupItemID<GroupID>;

      /** Whether the item can only be resolved once. */
      showOnce?: boolean;

      /** Returns this item's cooldown in milliseconds, replacing the group cooldown. */
      cooldown?: (state: State, now: number) => number;

      /** Returns the active recurrence ID, or undefined when inactive. */
      eventID?: (state: State, now: number) => string | undefined;

      /** Returns the active event start time used for priority ordering. */
      eventStartAt?: (state: State, now: number) => number | undefined;
    }[];
  };
